import { readChatStream } from './stream';
import { ProviderError, type ChatRequest, type ChatResult, type ChatUsage } from '../types';

/**
 * The Prakash Software endpoint is an Anthropic-Messages-compatible gateway that
 * accepts an Ollama-style model identifier and an extra `num_ctx` field.
 * Response shape matches Anthropic Messages — `content` is an array of
 * typed blocks (`text`, `thinking`, ...). We extract only the `text` blocks
 * so the visitor never sees the model's chain of thought.
 */
interface AnthropicTextBlock {
  type: 'text';
  text: string;
}
interface AnthropicThinkingBlock {
  type: 'thinking';
  thinking: string;
}
type AnthropicBlock = AnthropicTextBlock | AnthropicThinkingBlock | { type: string };

interface OpenlineResponse {
  id?: string;
  model?: string;
  content?: AnthropicBlock[];
  /** Some providers nest the assistant message OpenAI-style. */
  choices?: { message: { role: 'assistant'; content: string } }[];
  /** Anthropic-shaped or OpenAI-shaped, matching whichever body shape came back. */
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
  };
}

export async function chatOpenline(
  request: ChatRequest,
  config: { apiKey: string; baseUrl: string },
): Promise<ChatResult> {
  const { messages, opts } = request;
  const augmented = opts.systemPrompt
    ? [{ role: 'system' as const, content: opts.systemPrompt }, ...messages]
    : messages;

  // The Anthropic-style backend requires `max_tokens`. We also pass `num_ctx`
  // as in the user's reference curl.
  const body = {
    ...(opts.onDelta ? { stream: true } : {}),
    model: opts.model ?? 'qwen3:14b',
    messages: augmented,
    num_ctx: 65536,
    max_tokens: opts.maxTokens ?? 1024,
    temperature: opts.temperature,
    ...(opts.topP === undefined ? {} : { top_p: opts.topP }),
    ...(opts.stopSequences?.length ? { stop: opts.stopSequences } : {}),
  };

  const res = await fetch(`${config.baseUrl}/v1/ollama/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
    signal: opts.signal,
    redirect: 'error', // don't let a 3xx pivot to an internal endpoint
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new ProviderError('openline', res.status, errText || res.statusText, errText);
  }

  if (opts.onDelta && res.headers.get('content-type')?.includes('text/event-stream')) return readChatStream(res, 'openline', opts.onDelta);

  const data = (await res.json()) as OpenlineResponse;
  const text = extractText(data);
  const usage = extractUsage(data);
  return { text, raw: data, ...(usage ? { usage } : {}) };
}

function extractUsage(data: OpenlineResponse): ChatUsage | undefined {
  const u = data.usage;
  if (!u) return undefined;
  const inputTokens = u.input_tokens ?? u.prompt_tokens;
  const outputTokens = u.output_tokens ?? u.completion_tokens;
  if (inputTokens === undefined || outputTokens === undefined) return undefined;
  return { inputTokens, outputTokens };
}

function extractText(data: OpenlineResponse): string {
  // Anthropic shape: array of typed blocks. Only `text` blocks reach the user.
  if (Array.isArray(data.content)) {
    return data.content
      .filter((b): b is AnthropicTextBlock => b.type === 'text')
      .map((b) => b.text)
      .join('\n')
      .trim();
  }
  // OpenAI fallback shape.
  if (Array.isArray(data.choices)) {
    return data.choices[0]?.message?.content?.trim() ?? '';
  }
  return '';
}
